/**
 * Client-side reimplementation of the server's stroke + stableford math.
 *
 * Why duplicate it? Per-hole `strokes` and `stablefordPoints` on a
 * `PolfEventScore` come from getters that depend on `team.polfEvent`
 * being loaded — and the Scramble flight-scores query doesn't load it
 * on the score's `team` relation (only on the parent team row). As a
 * result, GraphQL returns `strokes: 0` / `stablefordPoints: 0` on every
 * per-hole row in Scramble.
 *
 * The fix without an API change: the top-level entry already exposes
 * `strokes` (the team's total — resolved against the well-loaded
 * parent team). We distribute that total across holes using the same
 * algorithm the API does, then compute stableford the same way.
 *
 * Mirrors `apps/api/src/util/scores.ts` getHoleStrokes +
 * getStablefordPoints. Keep in sync if either changes.
 */

/**
 * Distribute a total stroke allowance across 18 holes by hole index.
 * Positive allowances put the remainder on the hardest holes (index ≤
 * remainder); negative allowances subtract from the easiest holes
 * (index > 18 − remainder). Plus-handicap players are supported.
 */
export const getHoleStrokes = (strokes: number, holeIndex: number): number => {
  const base = strokes >= 0 ? Math.floor(strokes / 18) : Math.ceil(strokes / 18)
  const rem = Math.abs(strokes) % 18
  if (strokes >= 0) {
    return base + (holeIndex <= rem ? 1 : 0)
  }
  return base - (holeIndex > 18 - rem ? 1 : 0)
}

/**
 * Stableford for a single hole. Floored at 0 (can't go negative).
 */
export const getStablefordPoints = (
  par: number,
  grossScore: number,
  holeStrokes: number
): number => {
  const pts = par + holeStrokes - grossScore + 2
  return pts < 0 ? 0 : pts
}
